import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../services/config';

interface AdminRecord {
  id: string;
  name?: string;
  email?: string;
  status?: string;
  role?: string;
  department?: string;
  partner?: string;
  subscription_plan?: string;
  subscription_status?: string;
  projects?: number;
  budget?: number;
  description?: string;
  progress?: number;
  [key: string]: unknown;
}

export const useAdminData = () => {
  const [activeTab, setActiveTab] = useState('partners');
  const [partners, setPartners] = useState<AdminRecord[]>([]);
  const [clients, setClients] = useState<AdminRecord[]>([]);
  const [awsUsers, setAwsUsers] = useState<AdminRecord[]>([]);
  const [projects, setProjects] = useState<AdminRecord[]>([]);
  const [users, setUsers] = useState<AdminRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      switch (activeTab) {
        case 'partners': {
          const partnersRes = await fetch(`${API_BASE_URL}/partners`, { headers });
          if (partnersRes.ok) {
            const partnersData = await partnersRes.json();
            setPartners(partnersData);
          } else {
            const errorData = await partnersRes.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(`Failed to load partners: ${errorData.error || errorData.message || partnersRes.statusText}`);
          }
          break;
        }
        case 'clients': {
          const clientsRes = await fetch(`${API_BASE_URL}/clients`, { headers });
          if (clientsRes.ok) {
            const clientsData = await clientsRes.json();
            setClients(clientsData);
          } else {
            const errorData = await clientsRes.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(`Failed to load clients: ${errorData.error || errorData.message || clientsRes.statusText}`);
          }
          break;
        }
        case 'aws-users': {
          const awsUsersRes = await fetch(`${API_BASE_URL}/aws-users`, { headers });
          if (awsUsersRes.ok) {
            const awsUsersData = await awsUsersRes.json();
            setAwsUsers(awsUsersData);
          } else {
            const errorData = await awsUsersRes.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(`Failed to load AWS users: ${errorData.error || errorData.message || awsUsersRes.statusText}`);
          }
          break;
        }
        case 'projects': {
          const projectsRes = await fetch(`${API_BASE_URL}/projects`, { headers });
          if (projectsRes.ok) {
            const projectsData = await projectsRes.json();
            setProjects(projectsData);
          } else {
            const errorData = await projectsRes.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(`Failed to load projects: ${errorData.error || errorData.message || projectsRes.statusText}`);
          }
          break;
        }
        case 'users': {
          const usersRes = await fetch(`${API_BASE_URL}/users`, { headers });
          if (usersRes.ok) {
            const usersData = await usersRes.json();
            setUsers(usersData);
          } else {
            const errorData = await usersRes.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(`Failed to load users: ${errorData.error || errorData.message || usersRes.statusText}`);
          }
          break;
        }
        default:
          break;
      }
    } catch (err: unknown) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: AdminRecord) => {
    if (globalThis.confirm(`Are you sure you want to delete ${item.name}?`)) {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        let endpoint = '';
        switch (activeTab) {
          case 'partners':
            endpoint = `${API_BASE_URL}/partners/${item.id}`;
            break;
          case 'clients':
            endpoint = `${API_BASE_URL}/clients/${item.id}`;
            break;
          case 'aws-users':
            endpoint = `${API_BASE_URL}/aws-users/${item.id}`;
            break;
          case 'projects':
            endpoint = `${API_BASE_URL}/projects/${item.id}`;
            break;
          case 'users':
            endpoint = `${API_BASE_URL}/users/${item.id}`;
            break;
          default:
            return;
        }

        const response = await fetch(endpoint, {
          method: 'DELETE',
          headers
        });

        if (response.ok) {
          await loadData(); // Reload data after deletion
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(`Failed to delete: ${errorData.error || errorData.message || response.statusText}`);
        }
      } catch (err: unknown) {
        console.error('Error deleting item:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    }
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let endpoint = '';
      let method = 'POST';

      switch (activeTab) {
        case 'partners':
          endpoint = `${API_BASE_URL}/partners`;
          if (formData.id) {
            endpoint += `/${formData.id}`;
            method = 'PUT';
          }
          break;
        case 'clients':
          endpoint = `${API_BASE_URL}/clients`;
          if (formData.id) {
            endpoint += `/${formData.id}`;
            method = 'PUT';
          }
          break;
        case 'aws-users':
          endpoint = `${API_BASE_URL}/aws-users`;
          if (formData.id) {
            endpoint += `/${formData.id}`;
            method = 'PUT';
          }
          break;
        case 'projects':
          endpoint = `${API_BASE_URL}/projects`;
          if (formData.id) {
            endpoint += `/${formData.id}`;
            method = 'PUT';
          }
          break;
        case 'users':
          endpoint = `${API_BASE_URL}/users`;
          if (formData.id) {
            endpoint += `/${formData.id}`;
            method = 'PUT';
          }
          break;
        default:
          return;
      }

      const response = await fetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await loadData(); // Reload data after save
        return { success: true };
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Failed to save: ${errorData.error || errorData.message || response.statusText}`);
      }
    } catch (err: unknown) {
      console.error('Error saving:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return {
    activeTab,
    setActiveTab,
    partners,
    clients,
    awsUsers,
    projects,
    users,
    loading,
    error,
    loadData,
    handleDelete,
    handleSave
  };
};

export default useAdminData;
